import {
  BriefcaseIcon,
  ClockIcon,
  CogIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  UsersIcon
} from "@heroicons/react/24/outline";
import {
  BriefcaseIcon as BriefcaseSolid,
  ClockIcon as ClockSolid,
  CogIcon as CogSolid,
  HomeIcon as HomeSolid,
  UsersIcon as UsersSolid,
   ChatBubbleBottomCenterIcon as ChatBubbleBottomCenterSolidIcon,
  ClipboardDocumentListIcon as ClipboardDocumentListSolid,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { Client } from '@stomp/stompjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaHandshake, FaRegHandshake } from "react-icons/fa";
import { MdFeed, MdOutlineFeed, MdOutlinePendingActions } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import SockJS from 'sockjs-client';
import HOWLRLOGO from '../assest/HOWLRLOGO.png';
import HOWLRLOGOTwo from '../assest/HOWLRLOGOTwo.png';
import { getPendingProfilesCount, logoutUser, PROFILE_COUNT_WEBSOCKET_URL } from '../services/api';
import { Profile } from '../types/types';
import { isValidUrl } from '../utils/utilMethods';


interface SidebarProps {
  isExpanded: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isExpanded }) => {
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [showLogoutMenu, setShowLogoutMenu] = useState<boolean>(false);
  const [imageError, setImageError] = useState<boolean>(false);
  const [pendingCount, setPendingCount] = useState<number>(0);

  const [profileCountWsConnected, setProfileCountWsConnected] = useState<boolean>(false);
  const [userProfileType, setUserProfileType] = useState<string | null>(null);

  const { t, i18n } = useTranslation('sidebar');

  const isUserVerified = (): boolean => {
    const userJson = localStorage.getItem("loginResponse");
    const user = userJson ? JSON.parse(userJson) : null;
    return Boolean(user?.verified);
  };

  const verified = isUserVerified();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const profileCountStompClientRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const getCurrentUser = () => {
    const userJson = localStorage.getItem('loginResponse');
    return userJson ? JSON.parse(userJson) : null;
  };

  const fetchPendingCount = async (): Promise<void> => {
    try {
      const count = await getPendingProfilesCount();
      setPendingCount(count);
    } catch (error) {
      setPendingCount(0);
    }
  };

  const connectProfileCountWebSocket = (): void => {
    const currentUser = getCurrentUser();
    if (!currentUser?.id) {
      return;
    }

    const profileCountClient = new Client({
      webSocketFactory: () => new SockJS(PROFILE_COUNT_WEBSOCKET_URL),
      connectHeaders: {
        Authorization: `Bearer ${currentUser.token || ''}`,
      },
      debug: (str) => {
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    profileCountClient.onConnect = (frame) => {
      setProfileCountWsConnected(true);
      profileCountClient.subscribe(`/all/topic/profile-count`, (message) => {
        fetchPendingCount();
      });
      fetchPendingCount();
    };

    profileCountClient.onStompError = (frame) => {
      setProfileCountWsConnected(false);
    };

    profileCountClient.onWebSocketError = (error) => {
      setProfileCountWsConnected(false);
    };

    profileCountClient.onDisconnect = () => {
      setProfileCountWsConnected(false);
    };

    profileCountClient.activate();
    profileCountStompClientRef.current = profileCountClient;
  };

  const disconnectProfileCountWebSocket = (): void => {
    if (profileCountStompClientRef.current) {
      profileCountStompClientRef.current.deactivate();
      profileCountStompClientRef.current = null;
    }
    setProfileCountWsConnected(false);
  };

  useEffect(() => {
    const savedProfile = localStorage.getItem('loginResponse');
    if (savedProfile) {
      const parsedProfile = JSON.parse(savedProfile);
      setUserProfile(parsedProfile);
      setUserProfileType(parsedProfile.profileType);
    }
  }, []);


  useEffect(() => {
    fetchPendingCount();
    connectProfileCountWebSocket();

    const fallbackInterval = setInterval(() => {
      if (!profileCountWsConnected) {
        fetchPendingCount();
      }
    }, 60000);

    return () => {
      clearInterval(fallbackInterval);
      disconnectProfileCountWebSocket();
    };
  }, []);

  useEffect(() => {
  }, [profileCountWsConnected]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLogoutMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchPendingCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleProfileClick = (): void => {
    setShowLogoutMenu((prev) => !prev);
  };

  const handleViewProfile = (): void => {
    if (userProfile?.id) {

      setShowLogoutMenu(false);
      navigate(`/profile?profileId=${userProfile.id}`);
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      disconnectProfileCountWebSocket();

      await logoutUser();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.clear();
      navigate('/');
    }
  };

  const handleImageError = (): void => {
    setImageError(true);
  };

  const handleSettingsClick = (): void => {
    navigate('/settings');
  };

  const menuItems = [
    {
      name: t('home'),
      path: '/dashboard',
      icon: <HomeIcon className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <HomeSolid className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/dashboard'),
    },
    {
      name: t('athletesList'),
      path: '/fighters',
      icon: <UsersIcon className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <UsersSolid className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/fighters'),
    },
    {
      name: t('managerList'),
      path: '/manager',
      icon: <BriefcaseIcon className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <BriefcaseSolid className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/manager'),
    },
    {
      name: t('marketplaceCards'),
      path: '/marketplaceCards',
      icon: <ClipboardDocumentListIcon className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <ClipboardDocumentListSolid className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/marketplaceCards'),
    },
    {
      name: t('pendingmember'),
      path: '/pending',
      icon: <MdOutlinePendingActions className="w-5 h-5 flex-shrink-0 text-white" />,
      activeIcon: <MdOutlinePendingActions className="w-5 h-5 flex-shrink-0 text-white" />,
      onClick: () => navigate('/pending'),
      badge: pendingCount > 0 ? pendingCount : null,
    },
    {
      name: t('history'),
      path: '/history',
      icon: <ClockIcon className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <ClockSolid className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/history'),
    },
    {
      name: t('mydeals'),
      path: '/mydeals',
      icon: <FaRegHandshake className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <FaHandshake className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/mydeals'),
    },
    {
      name: t('abuseReport'),
      path: '/abuseReport',
      icon: <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-white" />,
      activeIcon: <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 text-white" />,
      onClick: () => navigate('/abuseReport'),
    },
    {
      name: t('feeds'),
      path: '/feeds',
      icon: <MdOutlineFeed className="w-5 h-5 flex-shrink-0" />,
      activeIcon: <MdFeed className="w-5 h-5 flex-shrink-0" />,
      onClick: () => navigate('/feeds'),
    },
  ];


  const isActiveRoute = (path: string): boolean => {
    return location.pathname === path;
  };

  return (
    <div
      className={`h-screen bg-[#0F172A] text-slate-100 flex flex-col border-r border-slate-800/80 transition-all duration-300 ease-in-out ${
        isExpanded ? 'w-52' : 'w-14'
      }`}
    >
      <div className="h-16 flex items-center border-b border-slate-800/80 px-4 space-x-3">
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
          <img src={HOWLRLOGO} alt="icon" className="w-6 h-6" />
        </div>
        <div
          className={`flex items-center transition-all duration-300 ease-in-out overflow-hidden ${
            isExpanded ? "opacity-100 w-24" : "opacity-0 w-0"
          }`}
        >
          <img src={HOWLRLOGOTwo} alt="icon" className="h-6 object-contain" />
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto p-2 custom-scrollbar"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#334155 transparent",
        }}
      >
        <div className="space-y-1.5">
          {menuItems.map((item, index) => {
            const isActive = isActiveRoute(item.path);
            return (
              <div
                key={index}
                className={`p-2.5 rounded-xl cursor-pointer transition-all duration-200 flex items-center relative ${
                  !isExpanded ? "justify-center" : ""
                } ${
                  isActive
                    ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow-md shadow-blue-500/25"
                    : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60"
                }`}
                onClick={item.onClick}
              >
                {isActive ? item.activeIcon : item.icon}
                {isExpanded && (
                  <span className={`ml-3 text-sm ${isActive ? "font-semibold" : "font-normal"}`}>
                    {item.name}
                  </span>
                )}

                {item.badge && (
                  <div
                    className={`absolute ${
                      isExpanded ? "right-3" : "top-1 right-1"
                    } bg-blue-500 text-white text-xs rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1 font-semibold shadow-sm`}
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-2.5 py-2 border-t border-slate-800/80">
        <div
          className={`flex items-center cursor-pointer p-2.5 rounded-xl transition-all duration-200 ${
            !isExpanded ? 'justify-center' : ''
          } ${
            isActiveRoute('/settings')
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow-md shadow-blue-500/25'
              : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/60'
          }`}
          onClick={handleSettingsClick}
        >
          {isActiveRoute('/settings') ? (
            <CogSolid className="w-5 h-5 flex-shrink-0" />
          ) : (
            <CogIcon className="w-5 h-5 flex-shrink-0" />
          )}
          {isExpanded && (
            <span className={`ml-3 text-sm ${isActiveRoute('/settings') ? 'font-semibold' : ''}`}>
              {t('settings')}
            </span>
          )}
        </div>
      </div>

      <div className="px-2.5 py-2.5 border-t border-slate-800/80 relative" ref={dropdownRef}>
        <div
          className={`flex items-center cursor-pointer hover:bg-slate-800/60 rounded-xl p-2 transition-all duration-200 border border-transparent hover:border-slate-700/50 ${
            !isExpanded ? 'justify-center' : 'justify-between'
          }`}
          onClick={handleProfileClick}
        >
          <div className="flex-shrink-0 w-8 h-8 relative">
            {(() => {
              const imageUrl = userProfile?.profileImage || userProfile?.profilePicture;
              if (!imageError && imageUrl && isValidUrl(imageUrl)) {
                return (
                  <img
                    src={imageUrl}
                    alt={`${userProfile?.firstName || 'User'} ${userProfile?.lastName || ''}`}
                    className="w-8 h-8 rounded-full object-cover relative ring-2 ring-blue-500/30"
                    onError={handleImageError}
                    referrerPolicy="no-referrer"
                  />
                );
              } else {
                return (
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                    {`${userProfile?.firstName?.[0]?.toUpperCase() || ''}${userProfile?.lastName?.[0]?.toUpperCase() || ''}` || 'U'}
                  </div>
                );
              }
            })()}
            {verified && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 flex items-center justify-center">
                <span className="w-4 h-4 rounded-full overflow-hidden">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 48 48"
                    className="w-full h-full"
                  >
                    <polygon
                      fill="#3b82f6"
                      points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                    />
                    <polygon
                      fill="#fff"
                      points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                    />
                  </svg>
                </span>
              </div>
            )}
          </div>

          {isExpanded && (
            <>
              <div className="ml-3 min-w-0 flex-1">
                <p className="font-medium text-slate-100 text-sm truncate">
                  {userProfile
                    ? `${userProfile.firstName || 'User'} ${userProfile.lastName || ''}`
                    : 'Loading...'}
                </p>
                <p className="text-slate-400 text-xs truncate font-medium">
                  {userProfile?.profileType || 'User'}
                </p>
              </div>
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                  showLogoutMenu ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </>
          )}
        </div>

        {showLogoutMenu && isExpanded && (
          <div className="absolute bottom-full left-3 right-3 mb-2 bg-slate-900 border border-slate-700/80 rounded-xl shadow-xl overflow-hidden z-50">
            {userProfile && (
              <Button
                variant="ghost"
                onClick={handleViewProfile}
                className="w-full px-4 py-3 text-left text-sm text-slate-200 hover:bg-slate-800 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
                {t('viewProfile')}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full px-4 py-3 text-left text-rose-400 hover:bg-slate-800 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('logout')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};